// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {ExternalLink} from '@app/features/app/components/shared/ExternalLink';
import {
	APP_STORE_NAME,
	BITWARDEN_AUTHENTICATOR_NAME,
	GOOGLE_PLAY_NAME,
	MFA_CODE_DIGIT_COUNT,
} from '@app/features/app/config/I18nDisplayConstants';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import * as MfaCommands from '@app/features/auth/commands/MfaCommands';
import {BackupCodesModal} from '@app/features/auth/components/modals/BackupCodesModal';
import styles from '@app/features/auth/components/modals/MfaTotpEnableModal.module.css';
import * as MfaUtils from '@app/features/auth/utils/MfaUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/Input';
import {QRCodeCanvas} from '@app/features/ui/components/QRCodeCanvas';
import type {User} from '@app/features/user/models/User';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';
import {useForm} from 'react-hook-form';

const BITWARDEN_AUTHENTICATOR_IOS_URL = 'https://apps.apple.com/us/app/bitwarden-authenticator/id6497335175';

const BITWARDEN_AUTHENTICATOR_ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.bitwarden.authenticator';
interface FormInputs {
	code: string;
}
interface MfaTotpEnableModalProps {
	user: User;
}
export const MfaTotpEnableModal = observer(({user}: MfaTotpEnableModalProps) => {
	const {t} = useLingui();
	const form = useForm<FormInputs>();
	const [secret] = useState(() => MfaUtils.generateTotpSecret());
	const onSubmit = async (data: FormInputs) => {
		const backupCodes = await MfaCommands.enableMfaTotp(
			MfaUtils.encodeTotpSecret(secret),
			data.code.split(' ').join(''),
		);
		ModalCommands.pop();
		ToastCommands.createToast({type: 'success', children: <Trans>Two-factor authentication enabled</Trans>});
		ModalCommands.pushWithKey(
			modal(() => <BackupCodesModal backupCodes={backupCodes} user={user} />),
			'backup-codes',
		);
	};
	const {handleSubmit} = useFormSubmit({
		form,
		onSubmit,
		defaultErrorField: 'code',
	});
	return (
		<Modal.Root size="small" centered>
			<Form form={form} onSubmit={handleSubmit} aria-label={t`Enable two-factor authentication form`}>
				<Modal.Header title={t`Setup Authenticator App`} />
				<Modal.Content>
					<Modal.ContentLayout>
						<div className={styles.qrContainer}>
							<QRCodeCanvas data={MfaUtils.encodeTotpSecretAsURL(user.email!, secret)} />
							<div className={styles.instructionsContainer}>
								<div>
									<Trans>
										Scan the QR code with your authenticator app to generate codes for two-factor authentication.
									</Trans>
								</div>
								<div className={styles.appRecommendation}>
									<Trans>
										We recommend {BITWARDEN_AUTHENTICATOR_NAME} if you do not already have an authenticator app. Install
										it from the <ExternalLink href={BITWARDEN_AUTHENTICATOR_IOS_URL}>{APP_STORE_NAME}</ExternalLink> or{' '}
										<ExternalLink href={BITWARDEN_AUTHENTICATOR_ANDROID_URL}>{GOOGLE_PLAY_NAME}</ExternalLink>, then
										scan this QR code. Other TOTP apps also work.
									</Trans>
								</div>
								<pre className={styles.secretText}>{secret}</pre>
							</div>
						</div>
						<Input
							{...form.register('code')}
							autoComplete="one-time-code"
							autoFocus={true}
							error={form.formState.errors.code?.message}
							label={t`Code`}
							required={true}
							footer={
								<Modal.Description>
									<Trans>Enter the {MFA_CODE_DIGIT_COUNT}-digit code from your authenticator app.</Trans>
								</Modal.Description>
							}
						/>
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer>
					<Button onClick={ModalCommands.pop} variant="secondary">
						<Trans>Cancel</Trans>
					</Button>
					<Button type="submit" submitting={form.formState.isSubmitting}>
						<Trans>Continue</Trans>
					</Button>
				</Modal.Footer>
			</Form>
		</Modal.Root>
	);
});
